import React, { useState } from 'react';

const SECBurstPollModal = ({ schedule, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: `${schedule.name} - Burst Poll`,
    start_date: '',
    start_time: '',
    duration_minutes: 30
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const durationOptions = [
    { value: 15, label: '15 minutes' },
    { value: 30, label: '30 minutes' },
    { value: 60, label: '1 hour' },
    { value: 120, label: '2 hours' },
    { value: 240, label: '4 hours' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'duration_minutes' ? parseInt(value, 10) : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Combine date and time into ISO format
      if (!formData.start_date || !formData.start_time) {
        setError('Please select both date and time');
        setLoading(false);
        return;
      }

      const startDateTime = new Date(`${formData.start_date}T${formData.start_time}`);
      
      // Validate it's in the future
      if (startDateTime <= new Date()) {
        setError('Start time must be in the future');
        setLoading(false);
        return;
      }

      const sessionData = {
        schedule_id: schedule.schedule_id,
        name: formData.name,
        start_time: startDateTime.toISOString(),
        duration_minutes: formData.duration_minutes
      };

      await onSubmit(sessionData);
      onClose();
    } catch (err) {
      console.error('Error creating burst session:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to create burst session');
    } finally {
      setLoading(false);
    }
  };

  // Get minimum date (today) for the date picker
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Get minimum time if the selected date is today
  const getMinTime = () => {
    if (!formData.start_date) return '';
    const today = new Date();
    const selectedDate = new Date(formData.start_date);
    
    if (selectedDate.toDateString() === today.toDateString()) {
      // If today, return current time + 5 minutes
      const minTime = new Date(today.getTime() + 5 * 60000);
      return minTime.toTimeString().slice(0, 5);
    }
    return '';
  };

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-lightning-charge me-2"></i>
              Schedule Burst Polling Session
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={loading}
            ></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  {error}
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setError('')}
                  ></button>
                </div>
              )}

              <div className="alert alert-info">
                <i className="bi bi-info-circle me-2"></i>
                <strong>What is Burst Polling?</strong>
                <p className="mb-0 mt-2">
                  Burst polling runs every minute during the specified time window, 
                  ideal for catching filings as soon as they drop (e.g., earnings reports).
                </p>
              </div>

              <div className="mb-3">
                <label className="form-label">Schedule</label>
                <input
                  type="text"
                  className="form-control"
                  value={schedule.name}
                  disabled
                />
                <div className="form-text">
                  Companies: {schedule.cik_numbers.join(', ')} | 
                  Filing Types: {schedule.filing_types.join(', ')}
                </div>
              </div>

              <div className="mb-3">
                <label htmlFor="name" className="form-label">Session Name *</label>
                <input
                  type="text"
                  className="form-control"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  maxLength={100}
                />
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="start_date" className="form-label">Start Date *</label>
                  <input
                    type="date"
                    className="form-control"
                    id="start_date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleChange}
                    min={getMinDate()}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label htmlFor="start_time" className="form-label">Start Time *</label>
                  <input
                    type="time"
                    className="form-control"
                    id="start_time"
                    name="start_time"
                    value={formData.start_time}
                    onChange={handleChange}
                    min={getMinTime()}
                    required
                  />
                  <div className="form-text">Time is in your local timezone</div>
                </div>
              </div>

              <div className="mb-3">
                <label htmlFor="duration_minutes" className="form-label">Duration *</label>
                <select
                  className="form-select"
                  id="duration_minutes"
                  name="duration_minutes"
                  value={formData.duration_minutes}
                  onChange={handleChange}
                  required
                >
                  {durationOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <div className="form-text">
                  Polling will run every minute for this duration
                </div>
              </div>

              {formData.start_date && formData.start_time && (
                <div className="alert alert-secondary">
                  <strong>Summary:</strong>
                  <ul className="mb-0 mt-2">
                    <li>
                      <strong>Start:</strong> {new Date(`${formData.start_date}T${formData.start_time}`).toLocaleString()}
                    </li>
                    <li>
                      <strong>End:</strong> {new Date(new Date(`${formData.start_date}T${formData.start_time}`).getTime() + formData.duration_minutes * 60000).toLocaleString()}
                    </li>
                    <li>
                      <strong>Total Polls:</strong> ~{formData.duration_minutes} polls (1 per minute)
                    </li>
                  </ul>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-warning"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Scheduling...
                  </>
                ) : (
                  <>
                    <i className="bi bi-lightning-charge me-2"></i>
                    Schedule Burst Session
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SECBurstPollModal;
